import { useRef, type MouseEvent } from 'react';
import { Pressable } from 'react-native';

import { useFavoriteBlock } from '@/lib/favorites/use-favorite-block';

type FavoriteStarButtonProps = {
  blockId: string;
  disabled?: boolean;
  className?: string;
};

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={20}
      height={20}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={`pointer-events-none ${filled ? 'text-warning' : 'text-fg-muted'}`}
    >
      <path d="M12 2l2.955 6.052 6.595.961-4.77 4.649 1.126 6.571L12 17.77l-5.906 3.463 1.126-6.571-4.77-4.649 6.595-.961L12 2z" />
    </svg>
  );
}

export function FavoriteStarButton({ blockId, disabled, className }: FavoriteStarButtonProps) {
  const { isFavorite, toggling, toggle } = useFavoriteBlock(blockId);
  const skipClick = useRef(false);

  return (
    <Pressable
      aria-busy={toggling}
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={isFavorite}
      className={`flex h-8 w-8 items-center justify-center rounded-md hover:bg-surface-raised ${
        className ?? ''
      }`}
      disabled={disabled}
      style={{ zIndex: 2 }}
      onMouseDown={(event: MouseEvent<HTMLButtonElement>) => {
        if (event.button !== 0) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        skipClick.current = true;
        window.setTimeout(() => {
          skipClick.current = false;
        }, 500);
        void toggle();
      }}
      onPress={() => {
        if (skipClick.current) {
          return;
        }
        void toggle();
      }}
    >
      <StarIcon filled={isFavorite} />
    </Pressable>
  );
}
